import React, { useState, useEffect, ChangeEventHandler } from "react";
import moment from "moment";
import { useFormContext, get, useController } from "react-hook-form";

import "./TimeInput.scss";
import { FormattedMessage } from "react-intl";

type TimeInputProps = {
  name: string;
  defaultValue?: string;
  required?: boolean;
  validations?: object;
  inputFormat?: string;
  outputFormat?: string;
  registerOptions?: object;
  shouldUnregister?: boolean;
  onTimeSelect?: (time?: string) => void;
  disabled?: boolean;
};

function TimeInput(props: TimeInputProps) {
  const {
    name,
    defaultValue,
    required,
    validations,
    registerOptions,
    inputFormat,
    outputFormat,
    shouldUnregister,
    disabled,
    onTimeSelect = () => {},
  } = props;
  const [inputValue, setInputValue] = useState<string>("");
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { field } = useController({
    name,
    control,
    defaultValue,
    shouldUnregister,
    rules: {
      required,
      validate: {
        _validTime: (val) => isValidTime(val) || "Please enter a valid time",
        ...validations,
      },
      ...registerOptions,
    },
  });

  useEffect(() => {
    if (getValues(name)) {
      // for the case when after duplicating / editing getting time in milliseconds from server
      const timeMiliSeconds = moment(getValues(name), "HH:mm:ss.SSSSSS", true);
      if (timeMiliSeconds.isValid()) {
        setValue(name, timeMiliSeconds.format(outputFormat));
      }

      const time = moment(getValues(name), outputFormat, true);
      setInputValue(time.isValid() ? time.format(inputFormat) : getValues(name));
    } else {
      setInputValue("");
    }
  }, [getValues(name)]);

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setInputValue(e.target.value);
    const parsedValue = parseTimeValue(e.target.value);
    if (!e.target.value) {
      field.onChange("");
    } else {
      const time = moment(parsedValue, inputFormat, true);
      const outputTime = time.isValid() ? time.format(outputFormat) : parsedValue;
      field.onChange(outputTime);
      if (time.isValid()) {
        onTimeSelect(outputTime);
      }
    }
  };

  function parseTimeValue(val: any) {
    if (val.length === 4 && !isNaN(val)) {
      return `${val.slice(0, 2)}:${val.slice(2)}`;
    }
    return val;
  }

  const isValidTime = (val: string) => !val || moment(val, outputFormat, true).isValid();

  return (
    <div className="time-input-container">
      <input
        className={`time-input ${get(errors, name) ? "border-danger" : ""}`}
        placeholder={inputFormat?.toUpperCase()}
        value={inputValue}
        onChange={handleInputChange}
        ref={field.ref}
        onBlur={field.onBlur}
        name={field.name}
        disabled={disabled}
      />
      {get(errors, name) && (
        <div className="d-block font-b3 invalid-feedback" data-testid="time-input-error">
          {get(errors, name)?.message ? (
            get(errors, name).message
          ) : (
            <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
          )}
        </div>
      )}
    </div>
  );
}

export default TimeInput;

TimeInput.defaultProps = {
  required: false,
  validations: undefined,
  defaultValue: undefined,
  registerOptions: undefined,
  inputFormat: "HH:mm",
  outputFormat: "HH:mm:ss",
  onTimeSelect: undefined,
  shouldUnregister: false,
  disabled: false,
};
